import { useState, useEffect, useCallback } from 'react'
import { SupabaseClient } from '@supabase/supabase-js'

export interface Quiz {
  id: string
  question: string
  answers: string[]
  correct_index: number
  categories: string[] | null
  active: boolean
  created_at: string
}

export interface Greeting {
  id: string
  greeting: string
  active: boolean
  created_at: string
}

export function useQuizzes(client: SupabaseClient | null) {
  const [quizzes, setQuizzes] = useState<Quiz[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchQuizzes = useCallback(async () => {
    if (!client) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const { data, error: queryError } = await client
        .from('quizzes')
        .select('*')
        .order('created_at', { ascending: false })

      if (queryError) throw queryError
      setQuizzes(data || [])
    } catch (err: any) {
      console.error('Error fetching quizzes:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [client])

  useEffect(() => {
    fetchQuizzes()
  }, [fetchQuizzes])

  const toggleQuiz = useCallback(async (id: string, active: boolean) => {
    if (!client) return false

    try {
      const { error: updateError } = await client
        .from('quizzes')
        .update({ active })
        .eq('id', id)

      if (updateError) throw updateError
      await fetchQuizzes()
      return true
    } catch (err: any) {
      console.error('Error toggling quiz:', err)
      return false
    }
  }, [client, fetchQuizzes])

  return { quizzes, isLoading, error, refetch: fetchQuizzes, toggleQuiz }
}

export function useGreetings(client: SupabaseClient | null) {
  const [greetings, setGreetings] = useState<Greeting[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchGreetings = useCallback(async () => {
    if (!client) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const { data, error: queryError } = await client
        .from('greetings')
        .select('*')
        .order('created_at', { ascending: false })

      if (queryError) throw queryError
      setGreetings(data || [])
    } catch (err: any) {
      console.error('Error fetching greetings:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [client])

  useEffect(() => {
    fetchGreetings()
  }, [fetchGreetings])

  const toggleGreeting = useCallback(async (id: string, active: boolean) => {
    if (!client) return false

    try {
      const { error: updateError } = await client
        .from('greetings')
        .update({ active })
        .eq('id', id)

      if (updateError) throw updateError
      await fetchGreetings()
      return true
    } catch (err: any) {
      console.error('Error toggling greeting:', err)
      return false
    }
  }, [client, fetchGreetings])

  return { greetings, isLoading, error, refetch: fetchGreetings, toggleGreeting }
}
